import { memo, useCallback, useMemo, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { IconButton, cn } from '@cyphex/ui';
import { tokenize } from '../../lib/highlight';
import { LANGUAGE_LABEL, TOKEN_CLASS } from '../../lib/highlightRules';

interface CodeBlockProps {
  code: string;
  language?: string;
  className?: string;
}

const COPY_FEEDBACK_MS = 1_600;

/**
 * Fenced code with dependency-free highlighting. Tokenising is memoised on the
 * source string, so re-rendering a message costs nothing.
 */
export const CodeBlock = memo(function CodeBlock({ code, language = '', className }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);
  const tokens = useMemo(() => tokenize(code, language), [code, language]);
  const label = LANGUAGE_LABEL[language.toLowerCase()] ?? language.toUpperCase();

  const copy = useCallback(() => {
    void navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
    });
  }, [code]);

  return (
    <div className={cn('overflow-hidden rounded-lg border border-border bg-subtle', className)}>
      <div className="flex h-8 items-center justify-between border-b border-border bg-card px-2.5">
        <span className="text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
          {label || 'Code'}
        </span>
        <IconButton
          icon={copied ? Check : Copy}
          label={copied ? 'Copied' : 'Copy code'}
          size="xs"
          variant="ghost"
          onClick={copy}
        />
      </div>
      <pre className="overflow-x-auto p-3 text-base leading-5">
        <code className="font-mono">
          {tokens.map((token, index) => (
            <span key={`${index}-${token.kind}`} className={TOKEN_CLASS[token.kind]}>
              {token.text}
            </span>
          ))}
        </code>
      </pre>
    </div>
  );
});
